import React, { useEffect, useRef } from 'react';
import jump from 'jump.js';
import FilterSelect from '~components/Filters/components/FilterSelect';
import FormGroup from './components/FormGroup';
import { Query, ResponseData } from '~src/api/get/getObjects';
import InputNumber from './components/InputNumber';
import Chart from './components/Chart';
import { easeInOutQuad } from '~utils/easing';

import styles from './styles.module.css';

export type RoomSetting = {
  value: number;
  min: number;
  max: number;
  default?: { [employeeValue: number]: number }; // value - room value when employees.value >= key
};

export type AreaQuery = Query & Required<Pick<Query, 'areaFrom' | 'areaTo'>>;

export type RoomNames = 'meetingRooms' | 'offices' | 'kitchen';

type Label = {
  value: number;
  total: number;
  labelText: string;
  className?: string;
};

export type Props = {
  roomsList: ['meetingRooms', 'offices', 'kitchen'];
  employees: {
    value: number;
    min: number;
    max: number;
  };
  meetingRooms: RoomSetting;
  offices: RoomSetting;
  kitchen: RoomSetting;

  orderTarget?: string;

  areaForOnePerson: {
    value: number;
    options: [
      {
        value: number;
        label: string;
      },
      {
        value: number;
        label: string;
      },
      {
        value: number;
        label: string;
      },
    ];
  };
  areaForOneMeetingRoom: {
    value: number;
  };
  areaForOneOffice: {
    value: number;
  };
  areaForOneKitchen: {
    value: number;
  };
  query?: Omit<Query, 'areaFrom' | 'areaTo'>;
  totalOffices: number;
  totalKitchen: number;
  totalOpenSpace: number;
  totalMeetingRooms: number;
  totalArea: number;
  data: ResponseData;
  hasFirstRequestLoaded: boolean;
  decrementRoomValue(name: RoomNames): void;
  incrementRoomValue(name: RoomNames): void;
  setRoomValue(name: RoomNames, value: number): void;
  setEmployees(value: number): void;
  fetchObjects(): void;
  getQuery(): AreaQuery;
};

const Calculator = ({
  orderTarget,
  offices,
  kitchen,
  employees,
  meetingRooms,
  areaForOnePerson,
  decrementRoomValue,
  incrementRoomValue,
  setRoomValue,
  setEmployees,
  totalArea,
  totalKitchen,
  totalOffices,
  totalOpenSpace,
  totalMeetingRooms,
  fetchObjects,
}: Props) => {
  let orderTargetNodeRef = { current: undefined };
  
  if (process.browser) {
    orderTargetNodeRef = useRef(orderTarget && document.querySelector(orderTarget));
  }

  const labels: Label[] = [
    {
      value: employees.value,
      total: totalOpenSpace,
      labelText: `Опен спейс: на ${employees.value} чел.`,
      className: 'orange',
    },
    {
      value: offices.value,
      total: totalOffices,
      labelText: `Кабинеты: ${offices.value}`,
      className: 'red',
    },
    {
      value: meetingRooms.value,
      total: totalMeetingRooms,
      labelText: `Переговорные: ${meetingRooms.value}`,
      className: 'green',
    },
    {
      value: kitchen.value,
      labelText: `Кухни: ${kitchen.value}`,
      total: totalKitchen,
    },
  ];

  const area = {
    totalKitchen,
    totalOffices,
    totalOpenSpace,
    totalMeetingRooms,
  };

  const handleOnOrderClick = (e: React.MouseEvent) => {
    if (orderTargetNodeRef.current) {
      e.preventDefault();

      jump(orderTargetNodeRef.current, {
        duration: 1000,
        offset: 100,
        easing: easeInOutQuad,
      });
    }
  };

  const handleEmployeesChange = (_: string, value: number) =>
    setEmployees(value);

  useEffect(() => {
    fetchObjects();
  }, [totalArea]);

  return (
    <div className={styles['calculator__container']}>
      <div className={styles['calculator__form']}>
        <div className={styles['calculator-form__head']}>
          Калькулятор <br />
          офисных пространств
        </div>

        <div
          className={[
            styles['calculator-form__body'],
            styles['calculator-form__top'],
          ].join(' ')}
        >
          <FormGroup
            label='Количество сотрудников:'
            className={styles['form-group_half']}
          >
            <InputNumber
              value={employees.value}
              name='employees'
              setField={handleEmployeesChange}
            />
          </FormGroup>

          <FormGroup
            label='Площадь кв.м. на одного сотрудника:'
            className={styles['form-group_half']}
          >
            <FilterSelect
              {...areaForOnePerson}
              hideLabel
              onChange={setRoomValue}
              theme='clear'
              name='areaForOnePerson'
            />
          </FormGroup>
        </div>

        <div
          className={[
            styles['calculator-form__body'],
            styles['calculator-form__bottom'],
          ].join(' ')}
        >
          <FormGroup label='Кабинеты' className={styles['form-group_third']}>
            <InputNumber
              value={offices.value}
              name='offices'
              setField={setRoomValue}
              increment={incrementRoomValue}
              decrement={decrementRoomValue}
            />
          </FormGroup>

          <FormGroup
            label='Переговорные'
            className={styles['form-group_third']}
          >
            <InputNumber
              value={meetingRooms.value}
              name='meetingRooms'
              setField={setRoomValue}
              increment={incrementRoomValue}
              decrement={decrementRoomValue}
            />
          </FormGroup>

          <FormGroup label='Кухня' className={styles['form-group_third']}>
            <InputNumber
              value={kitchen.value}
              name='kitchen'
              setField={setRoomValue}
              increment={incrementRoomValue}
              decrement={decrementRoomValue}
            />
          </FormGroup>
        </div>
      </div>

      <div className={styles['calculator__info']}>
        <Chart area={area} total={totalArea}>
          <div className={styles['chart-params']}>
            <div>
              {labels.map(({ value, className, labelText, total }, index) => {
                if (value) {
                  return (
                    <div key={index}>
                      <div
                        className={[
                          styles['chart-info'],
                          styles['chart-info_' + className],
                        ].join(' ')}
                      >
                        <span>{labelText}</span>
                        <i>
                          {total} м<sup>2</sup>
                        </i>
                      </div>
                    </div>
                  );
                }
              })}
            </div>
            {orderTargetNodeRef.current && (
              <div className={styles['chart-params__button']}>
                <a
                  href='#'
                  onClick={handleOnOrderClick}
                  className='button button_green'
                >
                  <span> Оставить заявку </span>
                </a>
              </div>
            )}
          </div>
        </Chart>
      </div>
    </div>
  );
};

export default Calculator;
